/**
 * Embed types for Datadog context, used as a feature adoption marker:
 *
 * - 'standalone'          — loaded directly, not embedded
 * - 'iframe'              — legacy iframe embed (__epilot API / bundle.js)
 * - 'sdk-iframe'          — new Journey Embed SDK using .asIframe()
 * - 'web-component'       — raw <epilot-journey> element, manually added
 * - 'sdk-web-component'   — new Journey Embed SDK using .asWebComponent()
 */
export enum EmbedType {
  STANDALONE = 'standalone',
  IFRAME = 'iframe',
  SDK_IFRAME = 'sdk-iframe',
  WEB_COMPONENT = 'web-component',
  SDK_WEB_COMPONENT = 'sdk-web-component'
}

/** Module-level store for the resolved embed type. */
let _embedType: EmbedType | undefined

/** Store the embed type early (before DD is ready) so it can be sent later. */
export const setEmbedType = (type: EmbedType) => {
  _embedType = type
}

/**
 * Detects whether the app is running inside an iframe.
 */
const isInIframe = (): boolean => {
  try {
    return window.self !== window.top
  } catch (_error) {
    // cross-origin iframe — access to window.top throws
    return true
  }
}

/**
 * Resolves the embed type from the entry point and SDK marker.
 *
 * @param entry - Which entry point is calling: 'index' (standalone/iframe) or 'embed' (web component)
 * @param embedSource - The `embedSource` value from app params (set to 'sdk' by the Journey Embed SDK)
 */
export const resolveEmbedType = (
  entry: 'index' | 'embed',
  embedSource?: 'sdk' | undefined
): EmbedType => {
  if (_embedType) return _embedType

  const source =
    embedSource ??
    new URLSearchParams(window.location.search).get('embedSource')
  const isSDK = source === 'sdk'

  let embedType: EmbedType

  if (entry === 'embed') {
    embedType = isSDK ? EmbedType.SDK_WEB_COMPONENT : EmbedType.WEB_COMPONENT
  } else if (isInIframe()) {
    embedType = isSDK ? EmbedType.SDK_IFRAME : EmbedType.IFRAME
  } else {
    embedType = EmbedType.STANDALONE
  }

  setEmbedType(embedType)

  return embedType
}
